import React, { forwardRef } from 'react';
import { FaChevronDown, FaComments, FaPaperPlane, FaPlus, FaLightbulb, FaStar } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import ChatIntegrated from '../ChatIntegrated';

const MobileChatComponent = forwardRef(({
  mobileChatOpen,
  setMobileChatOpen,
  mobileChatExpanded,
  mobileChatMessage,
  setMobileChatMessage,
  handleMobileChatKeyPress,
  handleMobileChatToggle,
  handleMobileChatSend,
  canEditIdea,
  components,
  showComponentPicker,
  setShowComponentPicker,
  id,
  setComponents,
  refreshSidebar
}, chatRef) => {
  const handleMobileChatTextboxFocus = () => {
    setMobileChatOpen(true);
  };

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50">
      {/* Backdrop */}
      {mobileChatOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-300 z-40"
          onClick={() => setMobileChatOpen(false)}
        />
      )}
      
      {/* Chat Overlay */}
      <div 
        className={`absolute bottom-full left-0 right-0 bg-[var(--bg-primary)] border-t border-[var(--border-color)] shadow-lg transition-all duration-300 ease-in-out z-50 ${
          mobileChatOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full pointer-events-none'
        }`}
        style={{
          height: mobileChatExpanded ? 'calc(100vh - 120px)' : '500px'
        }}
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">AI Assistant</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMobileChatOpen(false)}
                className="p-2 rounded-lg hover:bg-[var(--bg-hover)] transition-colors"
              >
                <FaChevronDown className="text-[var(--text-secondary)]" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <ChatIntegrated 
              ref={chatRef}
              ideaId={id} 
              components={components} 
              setComponents={setComponents} 
              refreshSidebar={refreshSidebar} 
              mode="mobile"
              hideInput={true}
            />
          </div>
        </div>
      </div>
      
      {/* Bottom Toolbar */}
      <div className="bg-[var(--bg-secondary)] border-t border-[var(--border-color)] px-4 py-3 shadow-2xl drop-shadow-lg z-50 relative" 
           style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
        <div className="flex items-center gap-3" >
          {!mobileChatOpen && canEditIdea && (
            <button
              onClick={() => setShowComponentPicker(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all duration-200 hover:scale-105 active:scale-95"
            >
              <FaPlus className="text-sm" />
              <span className="text-sm font-medium">Add</span>
            </button>
          )}
          
          <div className={`flex-1 transition-all duration-300 ${
            mobileChatOpen ? 'space-y-3' : ''
          }`} style={{ maxWidth: '100%', width: '100%' }}>
          
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Ask about this idea..."
                value={mobileChatMessage}
                onChange={(e) => setMobileChatMessage(e.target.value)}
                onKeyPress={handleMobileChatKeyPress}
                className="mobile-chat-input w-full px-4 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-all text-base"
                onFocus={handleMobileChatTextboxFocus}
                style={{ fontSize: '16px' }} // Prevent zoom on iOS
              />
            </div>
            <button
              onClick={handleMobileChatToggle}
              className={`p-3 rounded-lg transition-all duration-200 ${
                mobileChatOpen 
                  ? 'bg-[var(--primary)] text-white shadow-lg' 
                  : 'bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:scale-105'
              }`}
            >
              {mobileChatOpen && mobileChatMessage.trim() ? (
                <FaPaperPlane className="text-lg" />
              ) : (
                <FaComments className="text-lg" />
              )}
            </button>
          </div>
          
          {/* Chat Suggestions - Only show when chat is open */}
          {mobileChatOpen && (
            <div className="flex gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-[var(--primary)] scrollbar-track-transparent pb-1" style={{ maxWidth: '100%', width: '100%' }}>
              <button
                onClick={() => {
                  setMobileChatMessage("Tell me about this idea");
                  handleMobileChatSend();
                }}
                className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-full text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] hover:border-[var(--primary)] transition-all duration-200 whitespace-nowrap touch-manipulation flex-shrink-0"
              >
                <FaLightbulb className="text-[var(--primary)]" />
                Tell me about this idea
              </button>
              
              {canEditIdea && (
                <button
                  onClick={() => {
                    setMobileChatMessage("Help me add a new component");
                    handleMobileChatSend();
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-full text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] hover:border-[var(--primary)] transition-all duration-200 whitespace-nowrap touch-manipulation flex-shrink-0"
                >
                  <FaPlus className="text-[var(--primary)]" />
                  Add a component
                </button>
              )}
              
              {components.length > 0 && (
                <button
                  onClick={() => {
                    const componentTypes = [...new Set(components.map(c => c.type))];
                    const firstType = componentTypes[0];
                    setMobileChatMessage(`Help me edit my ${firstType} component`);
                    handleMobileChatSend();
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-full text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] hover:border-[var(--primary)] transition-all duration-200 whitespace-nowrap touch-manipulation flex-shrink-0"
                >
                  <FiEdit2 className="text-[var(--primary)]" />
                  Edit components
                </button>
              )}
              
              <button
                onClick={() => {
                  setMobileChatMessage("What improvements can I make to this idea?");
                  handleMobileChatSend();
                }}
                className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-full text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] hover:border-[var(--primary)] transition-all duration-200 whitespace-nowrap touch-manipulation flex-shrink-0"
              >
                <FaStar className="text-[var(--primary)]" />
                Improve idea
              </button>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
});

MobileChatComponent.displayName = 'MobileChatComponent';

export default MobileChatComponent;
